import { useCallback, useEffect, useState } from 'react';

type Options = {
  /** Set both or neither, per the spec. */
  width?: number;
  height?: number;
};

/** Desktop Chromium only for now; Safari and Firefox have no support. */
const documentPictureInPictureSupported = () =>
  typeof window !== 'undefined' && 'documentPictureInPicture' in window;

/**
 * Opens a Document Picture-in-Picture window. `open` must be called from a user
 * gesture. Render into the returned window with `<PictureInPictureWindow>`.
 */
export const useDocumentPictureInPicture = ({
  width,
  height,
}: Options = {}) => {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);

  const open = useCallback(async () => {
    if (!window.documentPictureInPicture) return;
    setPipWindow(
      await window.documentPictureInPicture.requestWindow({
        width,
        height,
        preferInitialWindowPlacement: true,
      })
    );
  }, [width, height]);

  const close = useCallback(() => {
    setPipWindow(null);
  }, []);

  useEffect(() => {
    if (!pipWindow) return undefined;
    const onPageHide = () => setPipWindow(null);
    pipWindow.addEventListener('pagehide', onPageHide);
    return () => {
      pipWindow.removeEventListener('pagehide', onPageHide);
      // Closing an already closed window is a no-op.
      pipWindow.close();
    };
  }, [pipWindow]);

  return {
    supported: documentPictureInPictureSupported(),
    pipWindow,
    open,
    close,
  };
};
